import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Watch, Repeat, PiggyBank, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useCheckout } from "@/hooks/useCheckout";

const BILLING_ORDER = ["paid_1", "paid_2", "paid_4"];

export default function Pricing() {
  const { start } = useCheckout();
  const [data, setData] = useState(null);
  const [billing, setBilling] = useState("paid_1");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setData(data)).catch(() => setData(null));
  }, []);

  if (!data) return <section id="precios" className="py-24" />;

  const { watch, plan } = data;
  const opt = plan.billing_options[billing];
  const savingsVsBase = plan.annual - opt.plan_total;
  const paid1Total = plan.billing_options.paid_1.plan_total;
  const extraVs1 = opt.plan_total - paid1Total;

  const handleBuy = async () => {
    setLoading(true);
    await start("kiddio", billing);
    setLoading(false);
  };

  return (
    <section id="precios" className="bg-white border-b-2 border-[#12263A]">
      <div className="max-w-6xl mx-auto px-5 py-20 md:py-28">
        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#2E9EF0]">Precios</span>
        <h2 className="mt-3 text-[28px] md:text-[44px] font-black text-[#12263A] leading-[1.05] max-w-3xl" style={{ letterSpacing: "-0.02em" }}>
          Dos cosas, bien separadas: el reloj y el plan.
        </h2>
        <p className="mt-4 text-[17px] leading-[1.6] text-[#2A3D52] max-w-xl">
          El <strong className="text-[#12263A]">reloj</strong> es un producto que compras una vez. El <strong className="text-[#12263A]">plan</strong> es la membresía que lo mantiene conectado. Sin mezclas ni sorpresas.
        </p>

        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {/* WATCH — product */}
          <div className="border-2 border-[#12263A] bg-[#F3F2F2] p-8 flex flex-col" style={{ borderRadius: "32px" }} data-testid="watch-card">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#12263A] bg-white border-2 border-[#12263A] rounded-full px-3 py-1">Producto</span>
              <Watch size={20} strokeWidth={2} className="text-[#12263A]" />
            </div>
            <h3 className="mt-5 text-[24px] font-black text-[#12263A]">{watch.name}</h3>
            <div className="mt-3 flex items-end gap-2">
              <span className="text-[52px] font-black text-[#12263A] leading-none" style={{ letterSpacing: "-0.02em" }}>${watch.price.toFixed(0)}</span>
              <span className="text-[15px] font-bold text-[#2A3D52] mb-1">pago único</span>
            </div>
            <p className="mt-2 text-[14px] text-[#2A3D52]">Lo compras una vez y es tuyo para siempre.</p>
            <ul className="mt-6 space-y-3 flex-1">
              {watch.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[15px] text-[#12263A]">
                  <Check size={20} strokeWidth={2.5} className="text-[#3CB878] shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* PLAN — membership */}
          <div className="border-2 border-[#12263A] bg-[#2E9EF0] p-8 flex flex-col" style={{ borderRadius: "32px" }} data-testid="plan-card">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#12263A] bg-[#FFC533] border-2 border-[#12263A] rounded-full px-3 py-1">Membresía</span>
              <Repeat size={20} strokeWidth={2} className="text-[#12263A]" />
            </div>
            <h3 className="mt-5 text-[24px] font-black text-[#12263A]">{plan.name}</h3>

            {/* Live annual price for the selected option */}
            <div className="mt-3 flex items-end gap-3">
              <AnimatePresence mode="wait">
                <motion.span
                  key={billing}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.22 }}
                  className="text-[52px] font-black text-[#12263A] leading-none"
                  style={{ letterSpacing: "-0.02em" }}
                  data-testid="plan-annual-price"
                >
                  ${opt.plan_total.toFixed(2)}
                </motion.span>
              </AnimatePresence>
              <div className="mb-1.5 leading-tight">
                <span className="block text-[15px] font-bold text-[#12263A]">/año</span>
                <span className="block text-[13px] font-bold text-[#12263A]/60 line-through">${plan.annual.toFixed(2)}</span>
              </div>
            </div>
            <p className="mt-1 text-[13px] text-[#12263A]">Equivale a ${plan.monthly.toFixed(0)}/mes · red 4G incluida</p>

            {/* Savings badge — updates live */}
            <div className="mt-3 h-8">
              <AnimatePresence mode="wait">
                <motion.div
                  key={billing}
                  initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.22 }}
                  className="inline-flex items-center gap-2 bg-[#3CB878] border-2 border-[#12263A] rounded-full px-3 py-1"
                  data-testid="plan-savings"
                >
                  <PiggyBank size={16} strokeWidth={2.5} className="text-[#12263A]" />
                  <span className="text-[13px] font-bold text-[#12263A]">Ahorras ${savingsVsBase.toFixed(2)} al año</span>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* payment count selector */}
            <div className="mt-4 flex flex-wrap gap-2" data-testid="billing-toggle">
              {BILLING_ORDER.map((k) => {
                const o = plan.billing_options[k];
                const active = billing === k;
                return (
                  <button
                    key={k}
                    onClick={() => setBilling(k)}
                    data-testid={`billing-${k}`}
                    className={`px-4 py-2 rounded-[14px] border-2 border-[#12263A] font-bold text-[14px] transition-colors ${
                      active ? "bg-[#12263A] text-[#2E9EF0]" : "bg-white text-[#12263A] hover:bg-[#F3F2F2]"
                    }`}
                  >
                    {o.label}
                    <span className={`ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full border-2 border-[#12263A] ${active ? "bg-[#FFC533] text-[#12263A]" : "bg-[#3CB878] text-[#12263A]"}`}>
                      -{o.discount}%
                    </span>
                  </button>
                );
              })}
            </div>

            {/* consolidation nudge — encourages fewer payments */}
            <div className="mt-3 flex items-center gap-2 text-[13px] font-bold text-[#12263A]" data-testid="consolidation-nudge">
              {billing === "paid_1" ? (
                <>
                  <Sparkles size={16} strokeWidth={2.5} className="text-[#12263A]" />
                  <span>Máximo ahorro. ¡La mejor elección para tu bolsillo!</span>
                </>
              ) : (
                <>
                  <PiggyBank size={16} strokeWidth={2.5} className="text-[#12263A]" />
                  <span>Pagando en 1 solo pago ahorrarías ${extraVs1.toFixed(2)} más al año.</span>
                </>
              )}
            </div>

            <ul className="mt-6 space-y-3 flex-1">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-[15px] text-[#12263A]">
                  <Check size={20} strokeWidth={2.5} className="text-[#12263A] shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CLEAR breakdown of the first payment */}
        <div className="mt-6 border-2 border-[#12263A] bg-white p-7 md:p-8 max-w-3xl" style={{ borderRadius: "28px" }} data-testid="checkout-summary">
          <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#2E9EF0]">Tu primer pago hoy</span>
          <div className="mt-4 space-y-3">
            <Row label={`Reloj Kiddio`} sub="Producto · pago único" value={`$${opt.watch_price.toFixed(2)}`} />
            <Row
              label={`Plan Kiddio · ${opt.cadence}`}
              sub={`Membresía · ${opt.discount}% dto${opt.payments > 1 ? ` · primer pago de ${opt.payments}` : ""}`}
              value={`$${opt.per_payment.toFixed(2)}`}
            />
            <div className="h-[2px] bg-[#12263A]" />
            <div className="flex items-end justify-between">
              <span className="text-[17px] font-black text-[#12263A]">Total hoy</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={billing}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.22 }}
                  className="text-[36px] font-black text-[#12263A] leading-none" style={{ letterSpacing: "-0.02em" }}
                >
                  ${opt.total_now.toFixed(2)}
                </motion.span>
              </AnimatePresence>
            </div>
            <p className="text-[13px] text-[#6E7B8B]">{opt.note}</p>
            <div className="flex items-center gap-2 mt-1" data-testid="summary-savings">
              <PiggyBank size={15} strokeWidth={2.5} className="text-[#3CB878]" />
              <span className="text-[13px] font-bold text-[#12263A]">Con esta opción ahorras ${savingsVsBase.toFixed(2)} al año en el plan.</span>
            </div>
          </div>

          <button
            onClick={handleBuy}
            disabled={loading}
            data-testid="checkout-buy-btn"
            className="mt-6 w-full sm:w-auto px-8 py-4 rounded-[20px] bg-[#12263A] text-white border-2 border-[#12263A] font-bold hover:bg-[#2E9EF0] hover:text-[#12263A] transition-colors active:translate-y-0.5 disabled:opacity-60"
          >
            {loading ? "Un momento…" : "Comprar ahora"}
          </button>
          <p className="mt-3 text-[13px] text-[#6E7B8B]">Pago seguro con Stripe · Prueba con la tarjeta 4242 4242 4242 4242</p>
        </div>
      </div>
    </section>
  );
}

function Row({ label, sub, value }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <span className="block text-[16px] font-bold text-[#12263A]">{label}</span>
        <span className="block text-[13px] text-[#6E7B8B]">{sub}</span>
      </div>
      <span className="text-[18px] font-black text-[#12263A] whitespace-nowrap">{value}</span>
    </div>
  );
}
